import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';

import useApi from 'hooks/useApi';

export type AnnouncementsContextType = {
  // Whether the left-nav Announcements tab should render at all.
  visible: boolean;
  isSuperAdmin: boolean;
  // Drives the nav badge.
  unreadCount: number;
  loading: boolean;
  // Records that one announcement was opened. Safe to call on an already-read uid.
  markOneRead: (uid: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  // Re-asks the server for the badge without touching `loading`, so callers can keep it
  // in step with a list they have just loaded. One count query; safe to call often.
  refresh: () => void;
};

const AnnouncementsContext = createContext<AnnouncementsContextType | undefined>(undefined);

/**
 * Shared announcements read-state, for the nav badge and the announcements page.
 *
 * This is a context rather than a hook because the two consumers live in different
 * subtrees — Navigation sits in the drawer, the page sits in the routed outlet — and both
 * need the *same mutable* count. It replaces `useAnnouncementsVisibility`, whose
 * module-level promise cache was deliberately immutable for the whole session and so
 * could not express a number that changes as the user reads.
 *
 * The badge is fetched on app load and then re-asked on two events rather than polled: when
 * the tab regains focus, and whenever the announcements page opens (it calls `refresh`).
 *
 * That second one matters more than it looks. The feed refetches on every page open while
 * the badge used to be fetched exactly once per app load, so the two drifted apart the
 * moment anything was published mid-session — producing the one state that reads as
 * outright broken: a row wearing a "New" chip next to a nav item showing no number at all.
 * Anything that refetches the list now brings the badge with it.
 *
 * `isSuperAdmin` comes from the server rather than from matching role-name strings
 * client-side, and is still only a UI convenience — the write endpoints reject
 * unauthorised callers regardless of what this returns.
 */
export const AnnouncementsProvider = ({ children }: { children: ReactNode }) => {
  const { user, getAnnouncementVisibility, markAnnouncementRead, markAllAnnouncementsRead } = useApi();
  // Keyed by user so signing in as someone else in the same tab re-asks rather than
  // inheriting the previous account's answer.
  const userKey = user?.uid ?? user?.id ?? '';

  const [visible, setVisible] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  // Ticket for the newest in-flight request. Now that this can be called repeatedly, a
  // slow earlier response could otherwise land after a newer one and put a stale count
  // back on screen; anything whose ticket is no longer current is discarded.
  const requestId = useRef(0);

  // `silent` keeps `loading` untouched. A refresh must not flip it: the page renders its
  // empty state on `listLoading || visibilityLoading`, so a background re-ask would blink
  // a spinner over a perfectly good screen.
  const load = useCallback(
    (silent = false) => {
      if (!userKey) {
        setVisible(false);
        setIsSuperAdmin(false);
        setUnreadCount(0);
        setLoading(false);
        return;
      }

      const ticket = ++requestId.current;
      if (!silent) setLoading(true);

      getAnnouncementVisibility()
        .then((response: any) => {
          if (ticket !== requestId.current) return;
          const data = response?.data ?? {};
          setVisible(Boolean(data.visible));
          setIsSuperAdmin(Boolean(data.isSuperAdmin));
          setUnreadCount(Number(data.unreadCount) || 0);
          setLoading(false);
        })
        .catch(() => {
          if (ticket !== requestId.current) return;
          // A failed *first* load hides the tab: it is not load-bearing and the next
          // attempt re-asks. A failed refresh leaves what is already on screen alone —
          // dropping a working tab because one background call timed out would be a
          // worse outcome than a briefly stale count.
          if (!silent) {
            setVisible(false);
            setIsSuperAdmin(false);
            setUnreadCount(0);
          }
          setLoading(false);
        });
    },
    [userKey, getAnnouncementVisibility]
  );

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => load(true), [load]);

  // Catches the case no page can: the tab sat open while someone published. Bound to
  // focus rather than an interval so it costs nothing until the user actually comes back
  // to the window.
  useEffect(() => {
    if (!userKey) return;
    const onFocus = () => load(true);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [userKey, load]);

  // Takes the count from the server's response instead of doing `unreadCount - 1`. Only
  // the server knows whether this uid had already been read, or whether something was
  // published since the page loaded — decrementing locally drifts on both.
  const markOneRead = useCallback(
    async (uid: string) => {
      try {
        const response = await markAnnouncementRead(uid);
        const next = response?.data?.unreadCount;
        if (typeof next === 'number') setUnreadCount(next);
      } catch {
        // Swallowed deliberately: a badge that is briefly stale is not worth a toast on
        // top of the announcement the user just opened. It self-corrects on reload.
      }
    },
    [markAnnouncementRead]
  );

  const markAllRead = useCallback(async () => {
    try {
      await markAllAnnouncementsRead();
      setUnreadCount(0);
    } catch {
      // As above.
    }
  }, [markAllAnnouncementsRead]);

  return (
    <AnnouncementsContext.Provider value={{ visible, isSuperAdmin, unreadCount, loading, markOneRead, markAllRead, refresh }}>
      {children}
    </AnnouncementsContext.Provider>
  );
};

const useAnnouncements = () => {
  const context = useContext(AnnouncementsContext);
  if (!context) throw new Error('useAnnouncements must be used inside AnnouncementsProvider');
  return context;
};

export default useAnnouncements;
